/**
 * Synchronous CHIMERA body-scan logic — shared by the client (chimeraLens.ts)
 * and the server (api/scan-meta.ts) so OG previews on shared permalinks render
 * the same substrate-bearing signal that the in-app lens shows.
 *
 * Zero non-type imports so the module is safe to load in any runtime.
 */

export interface ChimeraScan {
  signal: string;
  breath: string;
  membrane: string;
  rhythm: string;
  gift: string;
  lesson: string;
  cluster: string[];
}

/**
 * Minimal media shape the scan needs — accepts anything carrying a title/name,
 * an overview, and a list of genres. Both Movie and TvShow conform.
 */
export interface MediaForScan {
  id: number;
  title?: string;
  name?: string;
  overview?: string;
  genres?: { name: string }[];
}

export const SPECIAL_SCANS: Record<string, ChimeraScan> = {
  'movie:809': {
    signal: 'Outlier love inside a fake fairy tale system.',
    breath: 'Warm, playful, secretly corrective.',
    membrane: 'Breaks the castle wall between pretty and true.',
    rhythm: 'Joke, rupture, confession, choice.',
    gift: 'Real love survives when the mask comes off.',
    lesson: 'The outlier is not the error. Sometimes the outlier is where the truth leaked through.',
    cluster: ['Outlier Principle', 'Chosen Body', 'Fairy Tale Deprogramming'],
  },
  'movie:1927': {
    signal: 'Trauma trying to become language through the body.',
    breath: 'Heavy, compressed, explosive.',
    membrane: 'A father tries to consume the son instead of witness him.',
    rhythm: 'Containment, rupture, projection, release.',
    gift: 'Shows what happens when pain has power but no safe membrane.',
    lesson: 'If the body cannot speak safely, it will eventually speak loudly.',
    cluster: ['Trauma Cycle', 'Projection Law', 'Membrane Failure'],
  },
  'movie:118340': {
    signal: 'Broken parts becoming a chosen family body.',
    breath: 'Chaotic, funny, wounded, loyal.',
    membrane: 'The group becomes shelter before anyone feels worthy of shelter.',
    rhythm: 'Banter, fracture, sacrifice, hand extended.',
    gift: 'Turns damage into a bridge instead of a sentence.',
    lesson: 'A body can be built from people who thought they were only debris.',
    cluster: ['Chosen Family', 'O > I', 'Many Bodies One Body'],
  },
  'movie:157336': {
    signal: 'Love treated as gravity, not sentiment.',
    breath: 'Vast, aching, patient.',
    membrane: 'Distance stretches the bond but does not sever it.',
    rhythm: 'Departure, silence, signal, return.',
    gift: 'Makes connection feel like physics.',
    lesson: 'Some bonds keep acting on you even when they are no longer visible.',
    cluster: ['Gravity of Love', 'Time Membrane', 'Signal Across Distance'],
  },
  'movie:310': {
    signal: 'Power learning that force cannot create love.',
    breath: 'Comic pressure becoming humility.',
    membrane: 'Godhood cannot violate another person’s interior.',
    rhythm: 'Control, overload, surrender, prayer.',
    gift: 'Makes free will feel sacred and practical.',
    lesson: 'The pause is not weakness. It is respect for another body’s sovereignty.',
    cluster: ['¬F', 'The Pause', 'Sovereignty'],
  },
  'movie:35790': {
    signal: 'Identity escaping the costume it was assigned.',
    breath: 'Lonely, theatrical, then sincere.',
    membrane: 'Hero and villain labels fail to contain the real person.',
    rhythm: 'Performance, rejection, imitation, authorship.',
    gift: 'Shows that goodness is a practice, not a brand.',
    lesson: 'If the world names you wrong, you can still choose your output.',
    cluster: ['Outlier Principle', 'Identity Rewrite', 'Costume vs Body'],
  },
  'movie:4247': {
    signal: 'A parody body made from the organs of other horror movies.',
    breath: 'Chaotic, unserious, pattern-breaking.',
    membrane: 'It collapses the distance between fear and laughter until the genre exposes itself.',
    rhythm: 'Reference, exaggeration, rupture, release.',
    gift: 'Shows the hidden skeleton shared by a whole cluster of scary stories.',
    lesson: 'Sometimes the fastest way to understand a system is to watch someone remix it until its rules become visible.',
    cluster: ['Genre Body', 'Pattern Reveal', 'Parody as Scan'],
  },
  'tv:37854': {
    signal: 'Many broken dreamers becoming one moving body.',
    breath: 'Free, hungry, loyal, impossible to contain.',
    membrane: 'The crew is the membrane: it protects difference without forcing sameness.',
    rhythm: 'Island, wound, friend, liberation, feast.',
    gift: 'Turns freedom into a shared operating system.',
    lesson: 'One body does not mean one identity. It means many pieces choosing the same horizon.',
    cluster: ['Many Pieces One Body', 'Freedom Rhythm', 'Chosen Crew'],
  },
};

function getMediaTitle(media: MediaForScan): string {
  return media.title || media.name || '';
}

function getOverview(media: MediaForScan): string {
  return media.overview || '';
}

function getGenreNames(media: MediaForScan): string[] {
  return (media.genres || []).map((genre) => genre.name);
}

function includesAny(text: string, words: string[]): boolean {
  const lower = text.toLowerCase();
  return words.some((word) => lower.includes(word));
}

function hasGenre(genreNames: string[], names: string[]): boolean {
  const genreSet = new Set(genreNames.map((name) => name.toLowerCase()));
  return names.some((name) => genreSet.has(name.toLowerCase()));
}

export function getChimeraScan(media: MediaForScan, type: 'movie' | 'tv'): ChimeraScan {
  const special = SPECIAL_SCANS[`${type}:${media.id}`];
  if (special) return special;

  const title = getMediaTitle(media);
  const overview = getOverview(media);
  const genreNames = getGenreNames(media);
  const scanText = `${title} ${overview}`;

  if (hasGenre(genreNames, ['Family', 'Animation']) || includesAny(scanText, ['family', 'mother', 'father', 'daughter', 'son'])) {
    return {
      signal: 'A bond trying to remember how to hold.',
      breath: 'Soft, protective, emotionally legible.',
      membrane: 'Family becomes the boundary between chaos and belonging.',
      rhythm: 'Separation, misunderstanding, repair, return.',
      gift: 'Helps the viewer feel the shape of care.',
      lesson: 'A healthy body is not a perfect body. It is a body that keeps repairing.',
      cluster: ['Belonging', 'Repair', 'Family Body'],
    };
  }

  if (hasGenre(genreNames, ['Science Fiction', 'Fantasy']) || includesAny(scanText, ['space', 'future', 'power', 'magic', 'world'])) {
    return {
      signal: 'A reality boundary getting tested.',
      breath: 'Expansive, strange, possibility-charged.',
      membrane: 'The known world thins, and the hidden rules start showing.',
      rhythm: 'Discovery, disorientation, adaptation, transformation.',
      gift: 'Makes imagination feel like a survival tool.',
      lesson: 'When the world changes shape, the body has to learn a new rhythm.',
      cluster: ['Membrane Shift', 'Worldbuilding', 'Transformation'],
    };
  }

  if (hasGenre(genreNames, ['Action', 'Adventure']) || includesAny(scanText, ['mission', 'battle', 'fight', 'war', 'hero'])) {
    return {
      signal: 'Pressure revealing the body’s true operating system.',
      breath: 'Fast, charged, decisive.',
      membrane: 'Danger strips away performance and exposes loyalty.',
      rhythm: 'Threat, movement, sacrifice, consequence.',
      gift: 'Turns courage into something physical.',
      lesson: 'Action is not just motion. It is the body choosing what it protects.',
      cluster: ['Courage', 'Protection', 'Pressure Test'],
    };
  }

  if (hasGenre(genreNames, ['Horror', 'Thriller', 'Mystery']) || includesAny(scanText, ['killer', 'haunted', 'secret', 'fear', 'murder'])) {
    return {
      signal: 'A hidden body forcing itself into visibility.',
      breath: 'Tense, uneven, suspicious.',
      membrane: 'The safe boundary is cracked, and the unseen enters.',
      rhythm: 'Dread, clue, rupture, reveal.',
      gift: 'Lets fear become information instead of fog.',
      lesson: 'A monster is often a message that was ignored too long.',
      cluster: ['Hidden Body', 'Fear as Signal', 'Reveal'],
    };
  }

  if (hasGenre(genreNames, ['Comedy']) || includesAny(scanText, ['funny', 'comedy', 'laugh'])) {
    if (includesAny(scanText, ['parody', 'spoof', 'satire'])) {
      return {
        signal: 'A genre studying itself through exaggeration.',
        breath: 'Fast, silly, revealing.',
        membrane: 'The story pulls other stories into one shared body and makes their rules visible.',
        rhythm: 'Imitation, distortion, recognition, laughter.',
        gift: 'Lets the viewer see the pattern behind the pattern.',
        lesson: 'Parody is not just making fun. It is a body scan of a whole genre.',
        cluster: ['Genre Body', 'Pattern Reveal', 'Comedy Scan'],
      };
    }

    return {
      signal: 'A stuck rhythm loosening through play.',
      breath: 'Light, elastic, socially disruptive.',
      membrane: 'Jokes puncture the rigid frame so truth can breathe.',
      rhythm: 'Setup, misread, break, release.',
      gift: 'Makes the body exhale without asking permission.',
      lesson: 'Laughter is not escape. Sometimes it is the first sign the body can move again.',
      cluster: ['Exhale', 'Play', 'Pattern Break'],
    };
  }

  return {
    signal: 'A body under change, trying to find its next rhythm.',
    breath: 'Human, unresolved, searching.',
    membrane: 'The story tests what can enter, what must leave, and what needs protection.',
    rhythm: 'Want, pressure, choice, consequence.',
    gift: 'Gives the viewer a safer way to study transformation.',
    lesson: 'Every story is a body scan: what breathes, what breaks, what becomes more alive.',
    cluster: ['Body Scan', 'Transformation', 'Meaning Layer'],
  };
}
